import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchTransactionHistoryReportAction,
  setLoading,
} from "../slices/transactionHistoryReportSlice";
import { FetchTransactionHistoryReportParams } from "../api/fetchTransactionHistoryReport/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

export interface TransactionHistoryReportFilters {
  searchTerm?: string;
  types?: string[];
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
}

function buildFilterSortParams(
  filters: TransactionHistoryReportFilters,
  sorting: SortingState,
): Omit<FetchTransactionHistoryReportParams, "page" | "pageSize"> {
  const params: Omit<FetchTransactionHistoryReportParams, "page" | "pageSize"> =
    {};
  if (filters.searchTerm) params.searchTerm = filters.searchTerm;
  if (filters.types?.length) params.types = filters.types;
  if (filters.statuses?.length) params.statuses = filters.statuses;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (sorting.length > 0) {
    params.sortBy = sorting[0].id;
    params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
  }
  return params;
}

export function useTransactionHistoryReport() {
  const dispatch = useAppDispatch();

  const { items, totalCount, totals, loading, error } = useSelector(
    (state: RootState) => state.transactionHistoryReport,
  );

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<TransactionHistoryReportFilters>({});

  useEffect(() => {
    dispatch(
      fetchTransactionHistoryReportAction({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...buildFilterSortParams(filters, sorting),
      }),
    );
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      dispatch(setLoading(true));
      setPagination(newPagination);
    },
    [dispatch],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      dispatch(setLoading(true));
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (
      key: keyof TransactionHistoryReportFilters,
      value: string | string[] | undefined,
    ) => {
      dispatch(setLoading(true));
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch],
  );

  return {
    items,
    totalCount,
    totals,
    loading,
    error,
    pagination,
    sorting,
    filters,
    exportParams: buildFilterSortParams(filters, sorting),
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
